import React, {
    createContext,
    ReactNode,
    useContext,
    useMemo,
    useState,
} from 'react';
import {
    CANCELLATION_POLICY_INITIAL_STATE,
    CONTRACT_DETAIL_INITIAL_STATE,
    ID_CONTRACT_STATUS_BLOCKED,
    MAIN_INFO_INITIAL_STATE,
    MEAL_PLAN_INITIAL_STATE,
    RATE_DATE_INITIAL_STATE,
    ROOM_INITIAL_STATE,
    SEASON_INITIAL_STATE,
} from '../../utils/contractUtils/contractUtils';
import { adaptStaticRateContractToState } from '../../services/contracts/contractDetailAdapters';
import {
    ContractDetailPanelNames,
    IContractCancellationPolicyState,
    IContractDetailState,
    IContractMealPlanState,
    IContractRateDateState,
    IContractRoomState,
    IContractSeasonState,
    IMainInfoContractDetailState,
} from '../../types/contracts/contractTypes';
import { useNotification } from '../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../utils/commonVariables/messages';
import {
    getContractDetailData,
    saveContractData,
} from '../../services/contracts/contractsServices';
import { useHistory } from 'react-router-dom';
import encodeBase64 from '../../utils/encodeBase64';
import routes from '../../utils/commonVariables/routes';

interface ContractDetailContextType {
    contract: IContractDetailState;
    isLoadingView: boolean;
    handleLoadingView: (loading: boolean) => void;
    isLoadingPanel: boolean;
    isMainInfoEmpty: boolean;
    isNewContract: boolean;
    isBlockedContract: boolean;
    getContract: (idContract: string) => Promise<void>;
    handleLoadingPanel: (loading: boolean) => void;
    handleChangeMainInfoEdition: (
        value: Partial<IMainInfoContractDetailState>
    ) => void;
    mainInfoEdition: IMainInfoContractDetailState;
    openedPanel: ContractDetailPanelNames | null;
    seasonEdition: IContractSeasonState;
    roomEdition: IContractRoomState;
    mealPlanEdition: IContractMealPlanState;
    cancellationPolicyEdition: IContractCancellationPolicyState;
    handleChangeSeasonEdition: (value: Partial<IContractSeasonState>) => void;
    handleOpenPanel: (panelName: ContractDetailPanelNames | null) => void;
    handleOpenMainInfoPanel: () => void;
    handleOpenSeasonPanel: (data?: Partial<IContractSeasonState>) => void;
    handleChangeMealPlanEdition: (
        value: Partial<IContractMealPlanState>
    ) => void;
    handleOpenMealPlanPanel: (data?: Partial<IContractMealPlanState>) => void;
    handleChangeRoomEdition: (value: Partial<IContractRoomState>) => void;
    handleOpenRoomPanel: (data?: Partial<IContractRoomState>) => void;
    handleSaveMainInfo: () => Promise<void>;
    handleSaveSeason: (season?: IContractSeasonState) => Promise<void>;
    handleSaveRoom: (room?: IContractRoomState) => Promise<void>;
    handleSaveMealPlan: (mealPlan?: IContractMealPlanState) => Promise<void>;
    handleChangeCancellationPolicyEdition: (
        value: Partial<IContractCancellationPolicyState>
    ) => void;
    handleOpenCancellationPoliciesPanel: (
        data?: Partial<IContractCancellationPolicyState>
    ) => void;
    handleSaveCancellationPolicy: (
        policy?: IContractCancellationPolicyState
    ) => Promise<void>;
    rateDateEdition: IContractRateDateState;
    handleChangeRateDateEdition: (
        value: Partial<IContractRateDateState>
    ) => void;
    handleOpenRateDatePanel: (data?: Partial<IContractRateDateState>) => void;
    handleSaveRateDate: (rate?: IContractRateDateState) => Promise<void>;
}

const ContextContractDetail = createContext<
    ContractDetailContextType | undefined
>(undefined);

export const ProviderContractDetail = ({
    children,
}: {
    children: ReactNode;
}) => {
    const history = useHistory();
    const { setErrorMessage, setSuccessMessage } = useNotification();

    const [contract, setContract] = useState<IContractDetailState>(
        CONTRACT_DETAIL_INITIAL_STATE
    );
    const [mainInfoEdition, setMainInfoEdition] =
        useState<IMainInfoContractDetailState>(MAIN_INFO_INITIAL_STATE);
    const [seasonEdition, setSeasonEdition] =
        useState<IContractSeasonState>(SEASON_INITIAL_STATE);
    const [roomEdition, setRoomEdition] =
        useState<IContractRoomState>(ROOM_INITIAL_STATE);
    const [mealPlanEdition, setMealPlanEdition] =
        useState<IContractMealPlanState>(MEAL_PLAN_INITIAL_STATE);
    const [cancellationPolicyEdition, setCancellationPolicyEdition] =
        useState<IContractCancellationPolicyState>(
            CANCELLATION_POLICY_INITIAL_STATE
        );
    const [rateDateEdition, setRateDateEdition] =
        useState<IContractRateDateState>(RATE_DATE_INITIAL_STATE);

    const [isLoadingView, setIsLoadingView] = useState(false);
    const [isLoadingPanel, setIsLoadingPanel] = useState(false);
    const [openedPanel, setOpenedPanel] =
        useState<ContractDetailPanelNames | null>(null);

    const handleLoadingView = (loading: boolean) => {
        setIsLoadingView(loading);
    };

    const getContract = async (idContract: string) => {
        setIsLoadingView(true);
        try {
            const {
                data: { Success, Result: _contract, Messages = [] },
            } = await getContractDetailData(idContract);
            if (Success && _contract)
                setContract(adaptStaticRateContractToState(_contract));
            else {
                let [{ Title: errorMessage }] = Messages;
                if (errorMessage.includes('auth')) {
                    errorMessage = FeedbackMessages.ERROR_UNAUTHORIZED;
                }
                setErrorMessage(errorMessage);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoadingView(false);
        }
    };

    const handleSaveMainInfo = async () => {
        setIsLoadingPanel(true);
        try {
            const res = await saveContractData(mainInfoEdition);
            if (res.data) {
                setContract(adaptStaticRateContractToState(res.data));
                handleOpenPanel(null);
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
                if (parseInt(mainInfoEdition.id) < 0) {
                    history.push(
                        `${routes.landingPrivate}${
                            routes.contracts
                        }/${encodeBase64(res.data.Id)}`
                    );
                }
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoadingPanel(false);
        }
    };

    const handleSaveSeason = async (
        season: IContractSeasonState = seasonEdition
    ) => {
        setIsLoadingPanel(true);
        try {
            const res = await saveContractData({
                ...contract,
                seasons: [season],
            });
            if (res.data) {
                setContract(adaptStaticRateContractToState(res.data));
                handleOpenPanel(null);
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoadingPanel(false);
        }
    };

    const handleSaveRoom = async (room: IContractRoomState = roomEdition) => {
        setIsLoadingPanel(true);
        try {
            const res = await saveContractData({
                ...contract,
                rooms: [room],
            });
            if (res.data) {
                setContract(adaptStaticRateContractToState(res.data));
                handleOpenPanel(null);
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoadingPanel(false);
        }
    };

    const handleSaveCancellationPolicy = async (
        policy: IContractCancellationPolicyState = cancellationPolicyEdition
    ) => {
        setIsLoadingPanel(true);
        try {
            const res = await saveContractData({
                ...contract,
                policies: [policy],
            });
            if (res.data) {
                setContract(adaptStaticRateContractToState(res.data));
                handleOpenPanel(null);
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoadingPanel(false);
        }
    };

    const handleSaveMealPlan = async (
        mealPlan: IContractMealPlanState = mealPlanEdition
    ) => {
        setIsLoadingPanel(true);
        try {
            const res = await saveContractData({
                ...contract,
                mealPlans: [mealPlan],
            });
            if (res.data) {
                setContract(adaptStaticRateContractToState(res.data));
                handleOpenPanel(null);
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoadingPanel(false);
        }
    };

    const handleSaveRateDate = async (
        rate: IContractRateDateState = rateDateEdition
    ) => {
        setIsLoadingPanel(true);
        try {
            const res = await saveContractData({
                ...contract,
                rateDates: [rate],
            });
            if (res.data) {
                setContract(adaptStaticRateContractToState(res.data));
                handleOpenPanel(null);
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoadingPanel(false);
        }
    };

    const handleOpenPanel = (panelName: ContractDetailPanelNames | null) => {
        setOpenedPanel(panelName);
    };

    const handleChangeMainInfoEdition = (
        value: Partial<IMainInfoContractDetailState>
    ) => {
        setMainInfoEdition(prevState => ({
            ...prevState,
            ...value,
        }));
    };

    const getMainInfoCopy = (): IMainInfoContractDetailState => {
        const keys = Object.keys(MAIN_INFO_INITIAL_STATE) as Array<
            keyof IMainInfoContractDetailState
        >;
        return Object.fromEntries(
            keys.map(key => [key, contract[key]])
        ) as unknown as IMainInfoContractDetailState;
    };

    const handleChangeSeasonEdition = (
        value: Partial<IContractSeasonState>
    ) => {
        setSeasonEdition(prevState => ({
            ...prevState,
            ...value,
        }));
    };

    const handleOpenSeasonPanel = (
        data: Partial<IContractSeasonState> = {}
    ) => {
        const detailData = {
            ...SEASON_INITIAL_STATE,
            ...data,
        };
        handleChangeSeasonEdition(detailData);

        handleOpenPanel(ContractDetailPanelNames.SEASONS);
    };

    const handleOpenMainInfoPanel = () => {
        handleChangeMainInfoEdition(getMainInfoCopy());
        handleOpenPanel(ContractDetailPanelNames.MAIN_INFO);
    };

    const handleChangeMealPlanEdition = (
        value: Partial<IContractMealPlanState>
    ) => {
        setMealPlanEdition(prevState => ({
            ...prevState,
            ...value,
        }));
    };

    const handleChangeRoomEdition = (value: Partial<IContractRoomState>) => {
        setRoomEdition(prevState => ({
            ...prevState,
            ...value,
        }));
    };

    const handleChangeCancellationPolicyEdition = (
        value: Partial<IContractCancellationPolicyState>
    ) => {
        setCancellationPolicyEdition(prevState => ({
            ...prevState,
            ...value,
        }));
    };

    const handleOpenRoomPanel = (data: Partial<IContractRoomState> = {}) => {
        const detailData = {
            ...ROOM_INITIAL_STATE,
            ...data,
        };
        handleChangeRoomEdition(detailData);
        handleOpenPanel(ContractDetailPanelNames.ROOMS);
    };

    const handleOpenMealPlanPanel = (
        data: Partial<IContractMealPlanState> = {}
    ) => {
        const detailData = {
            ...MEAL_PLAN_INITIAL_STATE,
            ...data,
        };
        handleChangeMealPlanEdition(detailData);
        handleOpenPanel(ContractDetailPanelNames.MEAL_PLANS);
    };

    const handleOpenCancellationPoliciesPanel = (
        data: Partial<IContractCancellationPolicyState> = {}
    ) => {
        const detailData = {
            ...CANCELLATION_POLICY_INITIAL_STATE,
            ...data,
        };
        handleChangeCancellationPolicyEdition(detailData);
        handleOpenPanel(ContractDetailPanelNames.CANCELLATION_POLICIES);
    };

    const handleChangeRateDateEdition = (
        value: Partial<IContractRateDateState>
    ) => {
        setRateDateEdition(prevState => ({
            ...prevState,
            ...value,
        }));
    };

    const handleOpenRateDatePanel = (
        data: Partial<IContractRateDateState> = {}
    ) => {
        const detailData = {
            ...RATE_DATE_INITIAL_STATE,
            ...data,
        };
        handleChangeRateDateEdition(detailData);
        handleOpenPanel(ContractDetailPanelNames.RATES);
    };

    const handleLoadingPanel = (loading: boolean) => {
        setIsLoadingPanel(loading);
    };

    const isMainInfoEmpty = useMemo(() => {
        const keys: Array<keyof IContractDetailState> = [
            'name',
            'description',
            'idSupplierHotel',
            'idRatePlan',
            'idRateScopeModel',
            'idCurrency',
            'activeFrom',
            'activeTo',
        ];

        return keys.every(key => !contract[key]);
    }, [contract]);

    const isNewContract = !contract.id || parseInt(contract.id) <= 0;

    return (
        <ContextContractDetail.Provider
            value={{
                contract,
                isLoadingView,
                isLoadingPanel,
                handleLoadingView,
                isMainInfoEmpty,
                isNewContract,
                isBlockedContract:
                    contract.idStatus === ID_CONTRACT_STATUS_BLOCKED,
                getContract,
                handleLoadingPanel,
                handleChangeMainInfoEdition,
                mainInfoEdition,
                openedPanel,
                handleOpenPanel,
                handleOpenMainInfoPanel,
                seasonEdition,
                mealPlanEdition,
                roomEdition,
                cancellationPolicyEdition,
                handleChangeSeasonEdition,
                handleOpenSeasonPanel,
                handleOpenMealPlanPanel,
                handleChangeMealPlanEdition,
                handleOpenRoomPanel,
                handleChangeRoomEdition,
                handleSaveMainInfo,
                handleSaveSeason,
                handleSaveRoom,
                handleSaveMealPlan,
                handleChangeCancellationPolicyEdition,
                handleOpenCancellationPoliciesPanel,
                handleSaveCancellationPolicy,
                rateDateEdition,
                handleChangeRateDateEdition,
                handleOpenRateDatePanel,
                handleSaveRateDate,
            }}
        >
            {children}
        </ContextContractDetail.Provider>
    );
};

export const useContractDetail = () => {
    const context = useContext(ContextContractDetail);
    if (context === undefined) {
        throw new Error(
            'useContractDetail must be used within a ProviderContractDetail'
        );
    }

    return context;
};

export const useContractDetailData = () => {
    const context = useContractDetail();
    if (context === undefined) {
        throw new Error(
            'useContractDetail must be used within a ProviderContractDetail'
        );
    }

    return context.contract;
};
